import React, {useEffect, useState, useRef, MutableRefObject} from "react";
import "./DetailsItem.css";
import BusinessEntity from "../../../models/BusinessEntity";
import Interchange from "../../../models/Interchange";
import Collapsible from "../../shared/collapsible/collapsible";
const logos = require.context('../../../img/logos', false);

interface iProps {
  businessEntity: BusinessEntity;
  expanded: boolean;
  zoomToHandler: Function;
  expandHandler?: Function;
  onMouseEnter?: Function;
  onMouseLeave?: Function;
  onContactUsClick: Function;
  interchangeLayer?: MutableRefObject<__esri.FeatureLayer | undefined>;
};

export default function DetailsItem(props: iProps) {
  const hasFetchedInterchanges = useRef(false);
  const [interchanges, setInterchanges] = useState<Interchange[]>([]);

  const defaultLogoKey = './WATCO.svg';
  const logoKey = logos.keys().find((key: string) => key.startsWith(`./${props.businessEntity.businessEntityId}.`));
  let logo = '';
  if (logoKey) {
    logo = logos(logoKey);
  } else {
    logo = logos(defaultLogoKey);
  }

  useEffect(() => {
    if (props.expanded && props.interchangeLayer?.current && !hasFetchedInterchanges.current) {
      hasFetchedInterchanges.current = true;
      props.interchangeLayer.current.queryFeatures({
        where: `railroad='${props.businessEntity.businessEntityId}'`,
        returnGeometry: false,
        outFields: ['*'],
        returnDistinctValues: true
      }).then(featureSet => {
        const interchanges = featureSet.features
          .map(feature => {
            return {
              classIInterchange: feature.attributes.class_1_interchange,
              switchingCapability: feature.attributes.switching_capability,
              f286kCompatible: feature.attributes.f286k_compatible,
              nonHazardousEmpty: feature.attributes.non_hazardous_empty,
              nonHazardousLoaded: feature.attributes.non_hazardous_loaded,
              hazardousEmpty: feature.attributes.hazardous_empty,
              hazardousLoaded: feature.attributes.hazardous_loaded2,
              cherryPicking: feature.attributes.cherry_picking,
              class1Code: feature.attributes.Class1Code
            } as Interchange
          });
        setInterchanges(interchanges);
      })
    }
  }, [props.expanded, props.interchangeLayer, props.businessEntity])


  const onItemClick = (evt: any) => {
    if (props.expandHandler) {
      props.expandHandler();
    }
  }

  const onContactUsClick = (evt: any) => {
    props.onContactUsClick();
    evt.stopPropagation();
  }

  const onZoomToClick = (evt: any) => {
    props.zoomToHandler();
    evt.stopPropagation();
  }

  const onMouseEnter = (evt: any) => {
    if (props.onMouseEnter) {
      props.onMouseEnter();
    }
  }

  const onMouseLeave = (evt: any) => {
    if (props.onMouseLeave) {
      props.onMouseLeave();
    }
  }

  return (
    <div className={`flex-column railcar-storage-details-item ${props.expanded ? 'expanded' : 'reduced'}`}
      onClick={onItemClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}>
      <div className="flex-row">
        <img className='thumbnail' src={logo} alt={props.businessEntity.publicName} />
        <div className='flex-column details'>
          <div className='public-name' >{props.businessEntity.publicName}</div>
          <div className='capacity'>Railcar Capacity: {props.businessEntity.capacity}</div>
          <div className="btn-container">
            <button className="link-btn small-font" onClick={onContactUsClick}>Contact Us</button>
            <button className="link-btn small-font" onClick={onZoomToClick}>Zoom To</button>
          </div>
        </div>
      </div>

      {props.expanded &&
        <div className="flex-column expanded-details">
          {interchanges.length === 0 &&
            <div>
              No associated interchanges.
            </div>
          }
          {interchanges.length > 0 &&
            <>
              <div className="interchange-title bold">Class I Interchanges</div>
              {interchanges.map((interchange, index) =>
                <Collapsible className="interchange-collapsible"
                  key={index}
                  buttonLabel={interchange.classIInterchange}
                  buttonClass="interchange-collapsible-btn">
                  <div key={index} className="flex-col interchange-table">
                    <div className="flex-row bold interchange-table-row">
                      <div>Class I Interchange</div>
                      <div>{interchange.classIInterchange}</div>
                    </div>
                    <div className="flex-row interchange-table-row">
                      <div>Switching Capability</div>
                      <div>{interchange.switchingCapability}</div>
                    </div>
                    <div className="flex-row interchange-table-row">
                      <div>286k Compatible</div>
                      <div>{interchange.f286kCompatible}</div>
                    </div>
                    <div className="flex-row interchange-table-row">
                      <div>Non-Hazardous Empty</div>
                      <div>{interchange.nonHazardousEmpty}</div>
                    </div>
                    <div className="flex-row interchange-table-row">
                      <div>Non-Hazardous Loaded</div>
                      <div>{interchange.nonHazardousLoaded}</div>
                    </div>
                    <div className="flex-row interchange-table-row">
                      <div>Hazardous Empty</div>
                      <div>{interchange.hazardousEmpty}</div>
                    </div>
                    <div className="flex-row interchange-table-row">
                      <div>Hazardous Loaded</div>
                      <div>{interchange.hazardousLoaded}</div>
                    </div>
                    <div className="flex-row interchange-table-row">
                      <div>Cherry Picking</div>
                      <div>{interchange.cherryPicking}</div>
                    </div>
                  </div>
                </Collapsible>
              )}
            </>
          }
        </div>
      }
    </div>
  )
}
